/**
 * User Management Page (Admin Only)
 * Allows admins to view, suspend, assign plans, and impersonate users
 */
import React, { useState, useEffect } from 'react';
import { Search, MoreVertical, Ban, UserCheck, Crown, LogIn, Loader2, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { DashboardLayout } from '../../components/dashboard/DashboardLayout';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { adminService } from '../../api/services';
import { storage } from '../../utils/storage';
import type { AdminUser } from '../../types';

export default function UserManagementPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  // Modals
  const [suspendModal, setSuspendModal] = useState<AdminUser | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [planModal, setPlanModal] = useState<AdminUser | null>(null);
  // 'stripe' means "hand control back to Stripe"; a plan name means admin override
  const [selectedPlanMode, setSelectedPlanMode] = useState<'stripe' | 'free' | 'pro' | 'business'>('stripe');
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [page, search]);

  // Close the dropdown if the user scrolls (fixed-position menu would drift otherwise)
  useEffect(() => {
    const close = () => { setMenuOpen(null); setMenuPosition(null); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, []);

  // Close the dropdown on any click outside it (the toggle button and menu items
  // handle their own state changes via data-menu-root, so this only fires for clicks elsewhere)
  useEffect(() => {
    if (menuOpen === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as Element).closest('[data-menu-root]')) {
        setMenuOpen(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await adminService.listUsers({
        page,
        per_page: 20,
        search: search || undefined,
      });
      setUsers(response.users);
      setTotalPages(response.total_pages);
      setTotal(response.total);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!suspendModal || !suspendReason.trim()) return;

    setActionLoading(suspendModal.id);
    try {
      await adminService.suspendUser(suspendModal.id, { reason: suspendReason });
      toast.success(`User ${suspendModal.email} suspended`);
      setSuspendModal(null);
      setSuspendReason('');
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnsuspend = async (user: AdminUser) => {
    setActionLoading(user.id);
    try {
      await adminService.unsuspendUser(user.id);
      toast.success(`User ${user.email} unsuspended`);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to unsuspend user');
    } finally {
      setActionLoading(null);
      setMenuOpen(null);
    }
  };

  const handleSavePlanModal = async () => {
    if (!planModal) return;

    setActionLoading(planModal.id);
    try {
      if (selectedPlanMode === 'stripe') {
        // Clear override — Stripe now manages the plan
        const result = await adminService.clearPlanOverride(planModal.id);
        toast.success(`${planModal.email} is now Stripe-managed (${result.plan_name})`);
      } else {
        await adminService.assignPlan(planModal.id, { plan_name: selectedPlanMode });
        toast.success(`Override set: ${planModal.email} → ${selectedPlanMode}`);
      }
      setPlanModal(null);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSyncFromStripe = async (user: AdminUser) => {
    setActionLoading(user.id);
    try {
      const result = await adminService.clearPlanOverride(user.id);
      toast.success(`Synced — ${user.email} is now on the ${result.plan_name} plan (Stripe managed)`);
      setMenuOpen(null);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to sync from Stripe');
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (user: AdminUser) => {
    setActionLoading(user.id);
    try {
      const token = await adminService.impersonateUser(user.id);
      // Store original admin token
      const originalToken = storage.getAccessToken();
      localStorage.setItem('admin_original_token', originalToken || '');

      // Set impersonation token
      storage.setAccessToken(token.access_token);

      toast.success(`Now impersonating ${user.email}`);
      window.location.href = '/dashboard';
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
      setActionLoading(null);
      setMenuOpen(null);
    }
  };

  const handleSendTestEmail = async (template: 'welcome' | 'verification' | 'password_reset') => {
    setEmailLoading(true);
    try {
      await adminService.sendTestEmail(template);
      toast.success(`Test email sent to your inbox`);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setEmailLoading(false);
    }
  };

  const getPlanBadgeVariant = (planName: string): 'plan-free' | 'plan-pro' | 'plan-business' => {
    switch (planName) {
      case 'business':
        return 'plan-business';
      case 'pro':
        return 'plan-pro';
      default:
        return 'plan-free';
    }
  };

  return (
    <DashboardLayout currentPage="admin">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h1 className="heading-1 mb-1">Admin</h1>
            <p className="text-[--clinqr-text-secondary]">{total} total users</p>
          </div>
          <div className="relative self-start" data-menu-root>
            <Button
              variant="secondary"
              onClick={() => setMenuOpen(menuOpen === -1 ? null : -1)}
              disabled={emailLoading}
            >
              {emailLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Test Email
            </Button>
            {menuOpen === -1 && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-[--clinqr-border] z-50">
                <button onClick={() => { handleSendTestEmail('welcome'); setMenuOpen(null); }} className="w-full px-4 py-2 text-left text-sm hover:bg-[--clinqr-bg-subtle]">Welcome</button>
                <button onClick={() => { handleSendTestEmail('verification'); setMenuOpen(null); }} className="w-full px-4 py-2 text-left text-sm hover:bg-[--clinqr-bg-subtle]">Verification</button>
                <button onClick={() => { handleSendTestEmail('password_reset'); setMenuOpen(null); }} className="w-full px-4 py-2 text-left text-sm hover:bg-[--clinqr-bg-subtle]">Password Reset</button>
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[--clinqr-text-secondary]" />
            <input
              type="text"
              placeholder="Search by email or ID..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-[--clinqr-border] rounded-lg focus:outline-none focus:ring-2 focus:ring-[--clinqr-primary]/20 focus:border-[--clinqr-primary]"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl border border-[--clinqr-border] overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[--clinqr-primary]" />
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-320px)]">
              <table className="min-w-full divide-y divide-[--clinqr-border]">
                <thead className="bg-[--clinqr-bg-subtle] border-b border-[--clinqr-border] sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[--clinqr-text-secondary] uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[--clinqr-text-secondary] uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[--clinqr-text-secondary] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[--clinqr-text-secondary] uppercase tracking-wider">
                      QR Codes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[--clinqr-text-secondary] uppercase tracking-wider">
                      Scans
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[--clinqr-text-secondary] uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-[--clinqr-text-secondary] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[--clinqr-border]">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-[--clinqr-bg-subtle]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[--clinqr-primary] flex items-center justify-center text-white text-sm font-medium">
                            {user.email.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-[--clinqr-text-primary]">{user.email}</p>
                            <p className="text-xs text-[--clinqr-text-secondary]">ID: {user.id}</p>
                          </div>
                          {user.is_admin && (
                            <Crown className="w-4 h-4 text-amber-500"/>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <Badge variant={getPlanBadgeVariant(user.subscription?.plan?.name || 'free')}>
                            {user.subscription?.plan?.display_name || 'Free'}
                          </Badge>
                          {user.subscription?.is_admin_override && (
                            <span className="text-xs text-amber-600 font-medium">Admin override</span>
                          )}
                          {user.subscription?.cancel_at_period_end && user.subscription?.current_period_end && (
                            <span className="text-xs text-red-500">
                              Cancels {new Date(user.subscription.current_period_end).toLocaleDateString()}
                            </span>
                          )}
                          {!user.subscription?.stripe_subscription_id && user.subscription?.current_period_end && !user.subscription?.cancel_at_period_end && (
                            <span className="text-xs text-gray-400">
                              Was active until {new Date(user.subscription.current_period_end).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_suspended ? (
                          <Badge variant="error">
                            <Ban className="w-3 h-3" /> Suspended
                          </Badge>
                        ) : user.is_verified ? (
                          <Badge variant="success">
                            <UserCheck className="w-3 h-3" /> Verified
                          </Badge>
                        ) : (
                          <Badge variant="warning">Unverified</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[--clinqr-text-secondary]">
                        {user.qr_code_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[--clinqr-text-secondary]">
                        {user.total_scans.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[--clinqr-text-secondary]">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="inline-block text-left" data-menu-root>
                          <button
                            onClick={(e) => {
                              if (menuOpen === user.id) {
                                setMenuOpen(null);
                                setMenuPosition(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setMenuPosition({
                                  top: rect.bottom + 8,
                                  right: window.innerWidth - rect.right,
                                });
                                setMenuOpen(user.id);
                              }
                            }}
                            className="p-2 hover:bg-[--clinqr-bg-subtle] rounded-lg transition-colors"
                            disabled={actionLoading === user.id}
                          >
                            {actionLoading === user.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <MoreVertical className="w-4 h-4 text-[--clinqr-text-secondary]" />
                            )}
                          </button>

                          {menuOpen === user.id && menuPosition && (
                            <div
                              className="fixed w-56 bg-white rounded-lg shadow-lg border border-[--clinqr-border] z-50"
                              style={{ top: menuPosition.top, right: menuPosition.right }}
                            >
                              <button
                                onClick={() => {
                                  setMenuOpen(null);
                                  setPlanModal(user);
                                  // Default: if there's an active override show the overridden plan;
                                  // otherwise default to "stripe" so the admin sees the current state.
                                  setSelectedPlanMode(
                                    user.subscription?.is_admin_override
                                      ? (user.subscription.plan?.name as 'free' | 'pro' | 'business') || 'free'
                                      : 'stripe'
                                  );
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-[--clinqr-bg-subtle] flex items-center gap-2"
                              >
                                <Crown className="w-4 h-4" />
                                Manage Plan
                              </button>
                              {/* Always show sync — works even for pre-migration records */}
                              {user.subscription?.stripe_subscription_id && (
                                <button
                                  onClick={() => handleSyncFromStripe(user)}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-[--clinqr-bg-subtle] flex items-center gap-2 text-[--clinqr-text-secondary]"
                                >
                                  <Crown className="w-4 h-4" />
                                  Sync from Stripe
                                </button>
                              )}

                              {!user.is_admin && (
                                <>
                                  <button
                                    onClick={() => {
                                      setMenuOpen(null);
                                      if (user.is_suspended) {
                                        handleUnsuspend(user);
                                      } else {
                                        setSuspendModal(user);
                                      }
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-[--clinqr-bg-subtle] flex items-center gap-2"
                                  >
                                    <Ban className="w-4 h-4" />
                                    {user.is_suspended ? 'Unsuspend' : 'Suspend'}
                                  </button>
                                  <button
                                    onClick={() => handleImpersonate(user)}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-[--clinqr-bg-subtle] flex items-center gap-2"
                                  >
                                    <LogIn className="w-4 h-4" />
                                    Impersonate
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-[--clinqr-border] flex items-center justify-between">
              <p className="text-sm text-[--clinqr-text-secondary]">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-[--clinqr-border] hover:bg-[--clinqr-bg-subtle] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-[--clinqr-border] hover:bg-[--clinqr-bg-subtle] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Suspend Modal */}
        {suspendModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-[--clinqr-text-primary] mb-2">Suspend User</h3>
              <p className="text-sm text-[--clinqr-text-secondary] mb-4">
                Suspending <strong>{suspendModal.email}</strong>. They will not be able to access their
                account.
              </p>
              <textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Reason for suspension..."
                className="w-full px-3 py-2 border border-[--clinqr-border] rounded-lg focus:outline-none focus:ring-2 focus:ring-[--clinqr-primary]/20 focus:border-[--clinqr-primary] mb-4"
                rows={3}
              />
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSuspendModal(null);
                    setSuspendReason('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleSuspend}
                  disabled={!suspendReason.trim() || actionLoading !== null}
                  className="flex-1"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Suspend'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Plan Modal */}
        {planModal && (() => {
          const hasStripe = !!planModal.subscription?.stripe_subscription_id;
          const isOverride = selectedPlanMode !== 'stripe';
          const options: { value: 'stripe' | 'free' | 'pro' | 'business'; label: string; description: string }[] = [
            {
              value: 'stripe',
              label: 'Stripe managed',
              description: hasStripe
                ? 'Restore Stripe control — the plan will be set from their active subscription and kept in sync automatically.'
                : 'No active Stripe subscription. Plan will be set to Free and managed by Stripe going forward.',
            },
            {
              value: 'free',
              label: 'Free (override)',
              description: 'Force Free tier regardless of what Stripe says. Useful for suspending access without cancelling their subscription.',
            },
            {
              value: 'pro',
              label: 'Pro (override)',
              description: 'Force Pro tier. Use for comps or correcting a missed payment webhook.',
            },
            {
              value: 'business',
              label: 'Business (override)',
              description: 'Force Business tier. Stripe billing is unaffected.',
            },
          ];
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold text-[--clinqr-text-primary] mb-1">Manage Plan</h3>
                <p className="text-sm text-[--clinqr-text-secondary] mb-4">
                  Plan control for <strong>{planModal.email}</strong>
                </p>

                <div className="space-y-2 mb-5">
                  {options.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedPlanMode === opt.value
                          ? opt.value === 'stripe'
                            ? 'border-[--clinqr-primary] bg-[--clinqr-primary]/5'
                            : 'border-amber-400 bg-amber-50'
                          : 'border-[--clinqr-border] hover:bg-[--clinqr-bg-subtle]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="planMode"
                        value={opt.value}
                        checked={selectedPlanMode === opt.value}
                        onChange={() => setSelectedPlanMode(opt.value)}
                        className="mt-0.5 accent-[--clinqr-primary]"
                      />
                      <div>
                        <p className={`text-sm font-medium ${
                          selectedPlanMode === opt.value && opt.value !== 'stripe' ? 'text-amber-700' : 'text-[--clinqr-text-primary]'
                        }`}>
                          {opt.label}
                          {opt.value === 'stripe' && planModal.subscription?.is_admin_override && (
                            <span className="ml-2 text-xs font-normal text-[--clinqr-primary]">(removes current override)</span>
                          )}
                        </p>
                        <p className="text-xs text-[--clinqr-text-secondary] mt-0.5">{opt.description}</p>
                      </div>
                    </label>
                  ))}
                </div>

                {isOverride && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-xs text-amber-700">
                    Stripe webhooks will <strong>not</strong> change this plan while an override is active.
                    Select "Stripe managed" above to remove it.
                  </div>
                )}

                <div className="flex gap-3">
                  <Button variant="secondary" onClick={() => setPlanModal(null)} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSavePlanModal}
                    disabled={actionLoading !== null}
                    className="flex-1"
                  >
                    {actionLoading
                      ? <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                      : selectedPlanMode === 'stripe' ? 'Restore Stripe Control' : 'Set Override'
                    }
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </DashboardLayout>
  );
}
